import { defineStore } from 'pinia';
import axios from 'axios';
import { useAuthStore } from './auth';
import { calculateLineAmountNumber, toBig } from '@/composables/useInvoiceCalculations';
import { CREDIT_NOTE_PREFIX, INVOICE_CURRENCY } from '@/constants/invoiceDefaults';

const STRAPI_URL = import.meta.env.VITE_STRAPI_URL ?? 'http://localhost:1337';
const DEFAULT_PAGE_SIZE = 100;
const MAX_NUMBER_SCAN_PAGES = 50;
const INVOICE_NUMBER_COLLATOR = new Intl.Collator('en', {
	numeric: true,
	sensitivity: 'base',
});

const normalizeDocumentType = (metadata = {}) => {
	const raw = String(metadata?.documentType || 'invoice').trim().toLowerCase();
	return raw === 'credit-note' ? 'credit-note' : 'invoice';
};

const parseNumericInvoiceNumber = (value) => {
	const raw = String(value ?? '').trim();
	if (!/^\d+$/.test(raw)) return null;
	const parsed = Number(raw);
	return Number.isSafeInteger(parsed) ? parsed : null;
};

const parseCreditNoteOrderIndex = (value) => {
	const raw = String(value ?? '').trim();
	const escapedPrefix = CREDIT_NOTE_PREFIX.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
	const match = raw.match(new RegExp(`^${escapedPrefix}(\\d+)\\/`, 'i'));
	if (!match?.[1]) return null;
	const parsed = Number(match[1]);
	return Number.isSafeInteger(parsed) ? parsed : null;
};

const parseDocumentOrderIndex = (value) =>
	parseNumericInvoiceNumber(value) ?? parseCreditNoteOrderIndex(value);

const sanitizeDocumentNumberPart = (value) =>
	String(value || '')
		.trim()
		.replace(/\s+/g, '-')
		.replace(/[^a-zA-Z0-9-]/g, '')
		.replace(/-+/g, '-')
		.replace(/^-+|-+$/g, '');

const buildCreditNoteNumber = ({
	nextInvoiceNumber = '',
	sourceInvoiceNumber = '',
	sourceDocumentId = '',
}) => {
	const nextPart = String(nextInvoiceNumber || '').trim();
	const sourcePart =
		sanitizeDocumentNumberPart(sourceInvoiceNumber) ||
		sanitizeDocumentNumberPart(sourceDocumentId) ||
		'UNKNOWN';
	if (!nextPart) {
		return `${CREDIT_NOTE_PREFIX}UNKNOWN/${sourcePart}`;
	}
	return `${CREDIT_NOTE_PREFIX}${nextPart}/${sourcePart}`;
};

const isUniqueConstraintError = (error) => {
	const message =
		error?.response?.data?.error?.message ||
		error?.response?.data?.error?.details?.errors?.[0]?.message ||
		error?.message ||
		'';
	return /unique|already exists|must be unique/i.test(String(message));
};

const sortInvoicesByNumberDesc = (items = []) => {
	if (!Array.isArray(items)) return [];
	return items.sort((a, b) => {
		const aNumber = a?.invoiceNumber ?? '';
		const bNumber = b?.invoiceNumber ?? '';
		if (!aNumber && !bNumber) return 0;
		if (!aNumber) return 1;
		if (!bNumber) return -1;
		const aOrderIndex = parseDocumentOrderIndex(aNumber);
		const bOrderIndex = parseDocumentOrderIndex(bNumber);
		if (aOrderIndex !== null && bOrderIndex !== null && aOrderIndex !== bOrderIndex) {
			return bOrderIndex - aOrderIndex;
		}
		if (aOrderIndex !== null && bOrderIndex === null) return -1;
		if (aOrderIndex === null && bOrderIndex !== null) return 1;
		return INVOICE_NUMBER_COLLATOR.compare(String(bNumber), String(aNumber));
	});
};

const toNumberOrNull = (value) => {
	if (value === null || value === undefined || value === '') return null;
	const raw = String(value).trim();
	try {
		const numeric = Number(toBig(raw));
		return Number.isFinite(numeric) ? numeric : null;
	} catch (error) {
		return null;
	}
};

const normalizeServiceGroupIds = (value) => {
	if (Array.isArray(value)) {
		return value.filter((id) => id !== null && id !== undefined && id !== '');
	}
	if (value === null || value === undefined || value === '') return [];
	return [value];
};

const normalizeIdList = (value) => normalizeServiceGroupIds(value);

const normalizeRangeSources = (range = {}) => {
	const sources = range?.sources && typeof range.sources === 'object' ? range.sources : null;
	if (sources) {
		const appointments = sources.appointments || {};
		const purchases = sources.purchases || {};
		const appointmentGroups = normalizeIdList(appointments.serviceGroupIds);
		const purchaseCos = normalizeIdList(purchases.cosIds);
		return {
			appointments: {
				enabled: Boolean(appointments.enabled ?? appointments.all ?? appointmentGroups.length),
				all: Boolean(appointments.all),
				serviceGroupIds: appointmentGroups,
			},
			purchases: {
				enabled: Boolean(purchases.enabled ?? purchases.all ?? purchaseCos.length),
				all: Boolean(purchases.all),
				cosIds: purchaseCos,
				// false when the range bills its purchases at gross, without the 15%.
				markup: purchases.markup !== false,
			},
		};
	}

	const legacyType = range?.sourceType || 'appointments';
	if (legacyType === 'purchases') {
		const cosIds = normalizeIdList(range?.cosId);
		return {
			appointments: { enabled: false, all: false, serviceGroupIds: [] },
			purchases: {
				enabled: true,
				all: !cosIds.length,
				cosIds,
				markup: true,
			},
		};
	}
	if (legacyType === 'custom') {
		return {
			appointments: { enabled: false, all: false, serviceGroupIds: [] },
			purchases: { enabled: false, all: false, cosIds: [], markup: true },
		};
	}
	const serviceGroupIds = normalizeIdList(range?.serviceGroupIds ?? range?.serviceGroupId);
	return {
		appointments: {
			enabled: true,
			all: !serviceGroupIds.length,
			serviceGroupIds,
		},
		purchases: { enabled: false, all: false, cosIds: [], markup: true },
	};
};

const deriveLegacySourceType = (sources = {}, fallback = 'appointments') => {
	const appointmentsEnabled = Boolean(sources?.appointments?.enabled);
	const purchasesEnabled = Boolean(sources?.purchases?.enabled);
	if (appointmentsEnabled && purchasesEnabled) return 'mixed';
	if (purchasesEnabled) return 'purchases';
	if (appointmentsEnabled) return 'appointments';
	return fallback;
};

const normalizeSnapshot = (snapshot) => {
	if (!snapshot || typeof snapshot !== 'object') return {};
	return { ...snapshot };
};

const normalizeOptionalText = (value) => String(value ?? '').trim();

const mapLegacyLine = (line = {}) => {
	const amountMode = (line.amountMode || '').toLowerCase() === 'direct' ? 'direct' : 'calculated';
	const hours = toNumberOrNull(line.hours);
	const rate = toNumberOrNull(line.rate);
	const directAmount = toNumberOrNull(line.directAmount ?? line.amount);
	
	// Build temporary item for calculation
	const tempItem = { hours, rate, directAmount, amountMode };
	
	// Use shared Big.js calculation for consistent precision
	let amount = toNumberOrNull(line.amount);
	if (amount === null) {
		amount = calculateLineAmountNumber(tempItem);
	}
	
	return {
		description: line.description || '',
		serviceId: line.serviceId ?? null,
		serviceName: line.serviceName ?? line.description ?? '',
		flatRef: line.flatRef ?? '',
		hours,
		rate,
		amountMode,
		directAmount: amountMode === 'direct' ? (directAmount ?? amount ?? 0) : null,
		amount: amount ?? 0,
	};
};

const normalizeLegacyId = (value) => {
	if (value === null || value === undefined || value === '') return null;
	return String(value);
};

const isLegacyCustomItem = (item = {}) => {
	if (!item || typeof item !== 'object') return false;
	if (item.isCustom) return true;
	const id = normalizeLegacyId(item.id);
	return id ? id.startsWith('range-custom-') : false;
};

const getLegacyCustomKey = (item = {}) => {
	const id = normalizeLegacyId(item.id);
	if (id) return `id:${id}`;
	const description = String(item.description || '').trim().toLowerCase();
	const amount = item.directAmount ?? item.amount;
	if (!description) return null;
	if (amount === null || amount === undefined || amount === '') return null;
	return `desc:${description}|amount:${amount}`;
};

const normalizeInvoiceContent = (raw = {}) => {
	// New shape can arrive either nested under invoiceContent or as the raw object itself
	const content =
		(raw && typeof raw === 'object' && raw.invoiceContent && typeof raw.invoiceContent === 'object')
			? raw.invoiceContent
			: raw;

	// Already in new shape
	if (content && typeof content === 'object' && (content.dateRanges || content.customDates || content.summary)) {
		return {
			currency: INVOICE_CURRENCY,
			vatRate: toNumberOrNull(content.vatRate) ?? null,
			buildingScope: content.buildingScope || 'all',
				dateRanges: Array.isArray(content.dateRanges)
					? content.dateRanges.map((range) => {
							const sources = normalizeRangeSources(range);
							return {
								dateFrom: range?.dateFrom || '',
								dateTo: range?.dateTo || '',
								sources,
								sourceType: deriveLegacySourceType(sources, range?.sourceType || 'appointments'),
								serviceGroupIds: sources.appointments.serviceGroupIds,
								serviceGroupId: sources.appointments.serviceGroupIds[0] ?? null,
								cosId: sources.purchases.cosIds[0] ?? null,
								excludedPurchaseIds: normalizeIdList(range?.excludedPurchaseIds),
								excludedAppointmentIds: normalizeIdList(range?.excludedAppointmentIds),
								includedPurchaseIds: normalizeIdList(range?.includedPurchaseIds),
								items: Array.isArray(range?.items) ? range.items : [],
							};
					  })
				: [],
			customDates: Array.isArray(content.customDates)
				? content.customDates.map((entry) => ({
						date: entry?.date || '',
						items: Array.isArray(entry?.items) ? entry.items : [],
				  }))
				: [],
			summary: {
				subtotal: toNumberOrNull(content.summary?.subtotal),
				vat: toNumberOrNull(content.summary?.vat),
				total: toNumberOrNull(content.summary?.total),
			},
		};
	}

	const legacy = typeof raw === 'object' ? raw : {};
	const dateRanges = [];
	const customDates = [];

	// Legacy date ranges & items
	if (Array.isArray(legacy.invoiceItemEntries)) {
		legacy.invoiceItemEntries.forEach((entry) => {
			const rangeItems = [];
			const entryCustomItems = Array.isArray(entry.customItems) ? entry.customItems : [];
			const entryCustomKeys = new Set(
				entryCustomItems.map(getLegacyCustomKey).filter(Boolean),
			);
			// Attach regular items by entryId if available
			if (Array.isArray(legacy.items)) {
				legacy.items
					.filter((item) => item.entryId === entry.id)
					.forEach((item) => {
						const customKey = getLegacyCustomKey(item);
						const isCustom = isLegacyCustomItem(item);
						if (entryCustomKeys.size && isCustom && customKey && entryCustomKeys.has(customKey)) {
							return;
						}
						rangeItems.push(mapLegacyLine(item));
					});
			}
			// Attach custom items inside the range
			entryCustomItems.forEach((item) => rangeItems.push(mapLegacyLine(item)));
				const sources = normalizeRangeSources({ sourceType: 'appointments' });
				dateRanges.push({
					dateFrom: entry.dateFrom || '',
					dateTo: entry.dateTo || '',
					sources,
					sourceType: deriveLegacySourceType(sources, 'appointments'),
					serviceGroupIds: sources.appointments.serviceGroupIds,
					serviceGroupId: sources.appointments.serviceGroupIds[0] ?? null,
					cosId: sources.purchases.cosIds[0] ?? null,
					excludedPurchaseIds: [],
					excludedAppointmentIds: [],
					includedPurchaseIds: [],
					items: rangeItems,
				});
		});
	}

	// Legacy standalone custom items
	if (Array.isArray(legacy.customItemEntries)) {
		const groupedByDate = {};
		legacy.customItemEntries.forEach((item) => {
			const date = item.date || '';
			if (!groupedByDate[date]) groupedByDate[date] = [];
			groupedByDate[date].push(mapLegacyLine(item));
		});
		Object.entries(groupedByDate).forEach(([date, items]) => {
			customDates.push({ date, items });
		});
	}

	const subtotal = toNumberOrNull(legacy.subtotal);
	const vat = toNumberOrNull(legacy.vat);
	const total = toNumberOrNull(legacy.total);

	return {
		currency: INVOICE_CURRENCY,
		vatRate: legacy.vatRate ? toNumberOrNull(legacy.vatRate) : null,
		buildingScope: legacy.buildingScope || 'all',
		dateRanges,
		customDates,
		summary: { subtotal, vat, total },
	};
};

const normalizeRelation = (payload) => {
	if (!payload) return null;
	const data = payload?.data || payload;
	if (!data) return null;
	const attributes = data.attributes || {};
	return {
		id: data.id ?? attributes.id ?? null,
		documentId: attributes.documentId ?? data.documentId ?? null,
		name: attributes.name ?? data.name ?? '',
	};
};

const normalizeInvoiceItem = (entry = {}) => {
	const attributes = entry?.attributes || entry || {};
	const rawContent = attributes.invoiceContent ?? attributes.data ?? {};
	return {
		id: entry?.id ?? attributes.id ?? null,
		documentId: attributes.documentId ?? entry?.documentId ?? null,
		invoiceNumber: attributes.invoiceNumber ?? '',
		invoiceStatus: attributes.invoiceStatus ?? 'unpaid',
		invoiceDate: attributes.invoiceDate ?? null,
		dueDate: attributes.dueDate ?? null,
		purchaseOrder: normalizeOptionalText(attributes.purchaseOrder),
		paidDate: attributes.paidDate ?? null,
		totalAmount: toNumberOrNull(attributes.totalAmount),
		notes: attributes.notes ?? '',
		clientSnapshot: normalizeSnapshot(attributes.clientSnapshot),
		invoiceContent: normalizeInvoiceContent(rawContent),
		metadata: attributes.metadata ?? {},
		createdAt: attributes.createdAt ?? null,
		updatedAt: attributes.updatedAt ?? null,
		client: normalizeRelation(attributes.client),
		building: normalizeRelation(attributes.building),
		listId:
			attributes.client_invoices_list?.data?.id ??
			attributes.client_invoices_list ??
			null,
		raw: entry,
	};
};

const sanitizeInvoiceContentForSave = (content = {}) => {
	const currency = INVOICE_CURRENCY;
	const vatRate = toNumberOrNull(content.vatRate);

	const mapLine = (line = {}) => {
		const amountMode = (line.amountMode || '').toLowerCase() === 'direct' ? 'direct' : 'calculated';
		return {
			description: line.description || '',
			serviceId: line.serviceId ?? null,
			serviceName: line.serviceName ?? line.description ?? '',
			flatRef: line.flatRef ?? '',
			hours: toNumberOrNull(line.hours),
			rate: toNumberOrNull(line.rate),
			amountMode,
			directAmount: amountMode === 'direct' ? toNumberOrNull(line.directAmount) : null,
			amount: toNumberOrNull(line.amount),
			manual: !!line.manual,
			buildingId: line.buildingId ?? null,
			buildingName: line.buildingName || '',
			vendorId: line.vendorId ?? null,
			vendorName: line.vendorName || '',
		};
	};

	const mapCollection = (collection = []) =>
		Array.isArray(collection) ? collection.map(mapLine) : [];

	const dateRanges = Array.isArray(content.dateRanges)
		? content.dateRanges.map((range) => {
				const sources = normalizeRangeSources(range);
				return {
					dateFrom: range?.dateFrom || '',
					dateTo: range?.dateTo || '',
					sources,
					sourceType: deriveLegacySourceType(sources, range?.sourceType || 'appointments'),
					serviceGroupIds: sources.appointments.serviceGroupIds,
					serviceGroupId: sources.appointments.serviceGroupIds[0] ?? null,
					cosId: sources.purchases.cosIds[0] ?? null,
					excludedPurchaseIds: normalizeIdList(range?.excludedPurchaseIds),
					excludedAppointmentIds: normalizeIdList(range?.excludedAppointmentIds),
					includedPurchaseIds: normalizeIdList(range?.includedPurchaseIds),
					items: mapCollection(range?.items),
				};
		  })
		: [];

	const customDates = Array.isArray(content.customDates)
		? content.customDates.map((entry) => ({
				date: entry?.date || '',
				items: mapCollection(entry?.items),
		  }))
		: [];

	const summary = {
		subtotal: toNumberOrNull(content.summary?.subtotal),
		vat: toNumberOrNull(content.summary?.vat),
		total: toNumberOrNull(content.summary?.total),
	};

	return { currency, vatRate, buildingScope: content.buildingScope || 'all', dateRanges, customDates, summary };
};

const sanitizePayloadForSave = (payload = {}) => {
	const safe = {};
	if (payload.invoiceNumber !== undefined) safe.invoiceNumber = payload.invoiceNumber || null;
	if (payload.invoiceStatus !== undefined) safe.invoiceStatus = payload.invoiceStatus || 'unpaid';
	if (payload.invoiceDate !== undefined) safe.invoiceDate = payload.invoiceDate || null;
	if (payload.dueDate !== undefined) safe.dueDate = payload.dueDate || null;
	if (payload.purchaseOrder !== undefined) safe.purchaseOrder = normalizeOptionalText(payload.purchaseOrder) || null;
	if (payload.paidDate !== undefined) safe.paidDate = payload.paidDate || null;
	if (payload.totalAmount !== undefined) safe.totalAmount = toNumberOrNull(payload.totalAmount);
	if (payload.notes !== undefined) safe.notes = payload.notes || '';
	if (payload.client !== undefined) safe.client = payload.client || null;
	if (payload.building !== undefined) safe.building = payload.building || null;
	if (payload.client_invoices_list !== undefined)
		safe.client_invoices_list = payload.client_invoices_list || null;
	if (payload.clientSnapshot !== undefined) safe.clientSnapshot = normalizeSnapshot(payload.clientSnapshot);
	if (payload.metadata !== undefined) safe.metadata = payload.metadata || {};

	if (payload.invoiceContent !== undefined) {
		const content = payload.invoiceContent || {};
		safe.invoiceContent = sanitizeInvoiceContentForSave(content);
	}

	return safe;
};

const normalizeId = (value) => {
	if (value === null || value === undefined) return null;
	return String(value);
};

const normalizeListId = (value) => {
	if (value && typeof value === 'object') {
		return normalizeId(value.id || value.documentId || value.value || null);
	}
	return normalizeId(value);
};

const normalizeListEntry = (entry = {}) => {
	const attributes = entry?.attributes || entry || {};
	return {
		id: normalizeId(entry?.id ?? attributes.id ?? null),
		documentId: attributes.documentId ?? entry?.documentId ?? null,
		name: attributes.name ?? '',
		raw: entry,
	};
};

const hasActiveFilters = (filters = {}) => {
	const search = (filters.search || '').trim();
	return Boolean(
		search ||
		(filters.status && filters.status !== 'all') ||
		filters.dateFrom ||
		filters.dateTo ||
		filters.clientId ||
		filters.buildingId,
	);
};

// Reads one page, or every page when filters are active, of a list's invoices, newest number first.
const requestListItems = async (store, listId, activeFilters, { page = 1, pageSize = DEFAULT_PAGE_SIZE, allPages = false } = {}) => {
	const shouldFetchAll = allPages || hasActiveFilters(activeFilters);
	const resolvedPageSize = Number.isFinite(pageSize) && pageSize > 0 ? pageSize : DEFAULT_PAGE_SIZE;
	const fetchPage = async (targetPage) => {
		const params = store.buildListQueryParams(listId, activeFilters, {
			page: targetPage,
			pageSize: resolvedPageSize,
		});
		return axios.get(`${STRAPI_URL}/api/client-invoices-items`, {
			headers: store.authHeaders(),
			params,
		});
	};

	const firstPage = shouldFetchAll ? 1 : page;
	const response = await fetchPage(firstPage);
	const firstEntries = response?.data?.data || [];
	const metaPagination = response?.data?.meta?.pagination || {};
	let allEntries = [...firstEntries];

	const pageCount = Number.isFinite(metaPagination.pageCount) && metaPagination.pageCount > 0
		? metaPagination.pageCount
		: 1;

	if (shouldFetchAll && pageCount > 1) {
		for (let nextPage = 2; nextPage <= pageCount; nextPage += 1) {
			// eslint-disable-next-line no-await-in-loop
			const nextResponse = await fetchPage(nextPage);
			allEntries = allEntries.concat(nextResponse?.data?.data || []);
		}
	}

	const items = allEntries.map(normalizeInvoiceItem);
	sortInvoicesByNumberDesc(items);
	const total = shouldFetchAll
		? allEntries.length
		: (Number.isFinite(metaPagination.total) ? metaPagination.total : items.length);
	const nextPageSize = shouldFetchAll
		? (total || resolvedPageSize)
		: (Number.isFinite(metaPagination.pageSize) ? metaPagination.pageSize : resolvedPageSize);
	const nextPage = shouldFetchAll
		? 1
		: (Number.isFinite(metaPagination.page) ? metaPagination.page : page);
	const nextPageCount = shouldFetchAll
		? 1
		: (Number.isFinite(metaPagination.pageCount) && metaPagination.pageCount > 0
			? metaPagination.pageCount
			: Math.max(1, Math.ceil(total / nextPageSize)));

	return {
		items,
		pagination: {
			page: nextPage,
			pageSize: nextPageSize,
			pageCount: nextPageCount,
			total,
		},
	};
};

export const useClientInvoicesStore = defineStore('clientInvoices', {
	state: () => ({
		lists: [],
		listsLoading: false,
		listsError: null,
		activeListId: null,
		itemsByListId: {},
		itemsLoadingByListId: {},
		paginationByListId: {},
		itemsErrorByListId: {},
		activeFiltersByListId: {},
		currentInvoiceCache: {},
		quotationInvoiceLinks: {},
		quotationInvoiceLinksLoading: false,
		quotationInvoiceLinksLoaded: false,
		quotationInvoiceLinksError: null,
	}),
	getters: {
		listOptions(state) {
			return state.lists.map((list) => ({
				id: list.id,
				name: list.name || `List ${list.id}`,
			}));
		},
		activeList(state) {
			return state.lists.find((list) => list.id === state.activeListId) || null;
		},
		itemsForActiveList(state) {
			if (!state.activeListId) return [];
			return state.itemsByListId[state.activeListId] || [];
		},
	},
	actions: {
		authHeaders() {
			const authStore = useAuthStore();
			return authStore.token ? authStore.requestHeaders.headers : {};
		},

		normalizeDateRange(from, to) {
			const parse = (value) => {
				if (!value) return null;
				const date = new Date(value);
				return Number.isNaN(date.getTime()) ? null : { date, raw: value };
			};

			let start = parse(from);
			let end = parse(to);

			if (start && end && start.date > end.date) {
				[start, end] = [end, start];
			}
			if (start && !end) {
				end = start;
			} else if (!start && end) {
				start = end;
			}

			return {
				from: start?.raw || '',
				to: end?.raw || '',
			};
		},

		async fetchLists() {
			if (this.listsLoading) return;
			try {
				this.listsLoading = true;
				this.listsError = null;
				const response = await axios.get(`${STRAPI_URL}/api/client-invoices-lists`, {
					headers: this.authHeaders(),
					params: {
						sort: 'name:asc',
					},
				});
				const entries = response?.data?.data || [];
				this.lists = entries.map(normalizeListEntry);
				if (!this.activeListId && this.lists.length) {
					this.activeListId = this.lists[0].id;
				}
			} catch (error) {
				console.error('Failed to fetch client invoice lists:', error);
				this.listsError = error?.message || 'Failed to load lists';
				throw error;
			} finally {
				this.listsLoading = false;
			}
		},

	async initialize() {
		if (this.lists.length) return;
		await this.fetchLists();
	},

	getInvoiceForQuotation(quotationId) {
		if (quotationId === null || quotationId === undefined) return null;
		return this.quotationInvoiceLinks[String(quotationId)] || null;
	},

	setQuotationInvoiceLinks(invoices = []) {
		const links = {};
		const candidates = Array.isArray(invoices) ? invoices : [];
		const createdAtValue = (invoice) => {
			const timestamp = Date.parse(invoice?.createdAt || invoice?.raw?.createdAt || '');
			return Number.isFinite(timestamp) ? timestamp : 0;
		};
		const fallbackOrder = (invoice) => {
			const rawId = invoice?.id;
			const numericId = Number(rawId);
			if (Number.isFinite(numericId)) return numericId;
			return parseDocumentOrderIndex(invoice?.invoiceNumber) ?? 0;
		};

		candidates.forEach((invoice) => {
			const quotationId = invoice?.metadata?.sourceQuotation?.documentId;
			if (quotationId === null || quotationId === undefined || quotationId === '') return;
			const key = String(quotationId);
			const existing = links[key];
			if (
				!existing ||
				createdAtValue(invoice) > createdAtValue(existing) ||
				(createdAtValue(invoice) === createdAtValue(existing) &&
					fallbackOrder(invoice) > fallbackOrder(existing))
			) {
				links[key] = invoice;
			}
		});
		this.quotationInvoiceLinks = links;
		return links;
	},

	async fetchQuotationInvoiceLinks({ force = false } = {}) {
		if (this.quotationInvoiceLinksLoaded && !force) {
			return this.quotationInvoiceLinks;
		}

		this.quotationInvoiceLinksLoading = true;
		this.quotationInvoiceLinksError = null;
		try {
			await this.initialize();
			const fetchedInvoices = [];
			for (const list of this.lists) {
				let page = 1;
				let pageCount = 1;
				do {
					// Keep this lookup independent from the currently opened invoice journal.
					// eslint-disable-next-line no-await-in-loop
					const snapshot = await this.fetchItemsSnapshot(list.id, {
						page,
						pageSize: DEFAULT_PAGE_SIZE,
					});
					fetchedInvoices.push(...(snapshot.items || []));
					pageCount = Number(snapshot.pagination?.pageCount) || 1;
					page += 1;
				} while (page <= pageCount);
			}

			const cachedInvoices = Object.values(this.currentInvoiceCache || {});
			const listedInvoices = Object.values(this.itemsByListId || {}).flatMap((items) =>
				Array.isArray(items) ? items : [],
			);
			const links = this.setQuotationInvoiceLinks([
				...fetchedInvoices,
				...listedInvoices,
				...cachedInvoices,
			]);
			this.quotationInvoiceLinksLoaded = true;
			return links;
		} catch (error) {
			this.quotationInvoiceLinksError =
				error?.response?.data?.error?.message ||
				error?.message ||
				'Failed to load quotation invoice links';
			throw error;
		} finally {
			this.quotationInvoiceLinksLoading = false;
		}
	},

	setActiveList(listId) {
		const normalized = normalizeListId(listId);
		if (!normalized || this.activeListId === normalized) return;
		this.activeListId = normalized;
	},

	getActiveFilters(listId) {
			return (
				this.activeFiltersByListId[listId] || {
					search: '',
					status: 'all',
					dateFrom: '',
					dateTo: '',
				}
			);
		},

	setActiveFilters(listId, filters = {}) {
		const normalized = normalizeListId(listId);
		if (!normalized) return;
		this.activeFiltersByListId[normalized] = {
			...this.getActiveFilters(normalized),
			...filters,
		};
	},

	buildListQueryParams(listId, filters = {}, pagination = {}) {
		const normalizedListId = normalizeListId(listId);
		if (!normalizedListId) return null;
		const params = {
			'populate[client][fields][0]': 'name',
			'populate[client][fields][1]': 'documentId',
			'populate[building][fields][0]': 'name',
			'populate[building][fields][1]': 'documentId',
			'populate[client_invoices_list][fields][0]': 'name',
			sort: 'invoiceNumber:desc',
			'filters[client_invoices_list][id][$eq]': normalizedListId,
			'pagination[page]': pagination.page ?? 1,
			'pagination[pageSize]': pagination.pageSize ?? DEFAULT_PAGE_SIZE,
		};

			if (filters.search) {
				params['filters[invoiceNumber][$containsi]'] = filters.search.trim();
			}
			if (filters.status && filters.status !== 'all') {
				params['filters[invoiceStatus][$eq]'] = filters.status;
			}
			const { from: normalizedFrom, to: normalizedTo } = this.normalizeDateRange(
				filters.dateFrom,
				filters.dateTo,
			);
			if (normalizedFrom) {
				params['filters[invoiceDate][$gte]'] = normalizedFrom;
			}
			if (normalizedTo) {
				params['filters[invoiceDate][$lte]'] = normalizedTo;
			}
			if (filters.clientId) {
				params['filters[client][id][$eq]'] = filters.clientId;
			}
			if (filters.buildingId) {
				params['filters[building][id][$eq]'] = filters.buildingId;
			}

			return params;
		},

	async fetchItemsForList(
		listId,
		{ filters = {}, page = 1, pageSize = DEFAULT_PAGE_SIZE, allPages = false } = {},
	) {
		const normalizedListId = normalizeListId(listId);
		if (!normalizedListId) return;
		this.setActiveList(normalizedListId);
		this.setActiveFilters(normalizedListId, filters);

		this.itemsLoadingByListId[normalizedListId] = true;
		this.itemsErrorByListId[normalizedListId] = null;
		try {
			const { items, pagination } = await requestListItems(this, normalizedListId, this.getActiveFilters(normalizedListId), {
				page,
				pageSize,
				allPages,
			});
			this.itemsByListId[normalizedListId] = items;
			this.paginationByListId[normalizedListId] = pagination;
			return { items, pagination: this.paginationByListId[normalizedListId] };
		} catch (error) {
			console.error('Failed to fetch client invoices:', error);
			this.itemsErrorByListId[normalizedListId] =
				error?.response?.data?.error?.message ||
				error?.message ||
				'Failed to load invoices';
			throw error;
		} finally {
			this.itemsLoadingByListId[normalizedListId] = false;
		}
	},

	// Reads the list's current page again without touching it, so the page can decide whether it
	// is safe to show what changed elsewhere.
	async peekItemsForList(listId, { page = 1, pageSize = DEFAULT_PAGE_SIZE, allPages = false } = {}) {
		const normalizedListId = normalizeListId(listId);
		if (!normalizedListId) return null;
		return requestListItems(this, normalizedListId, this.getActiveFilters(normalizedListId), {
			page,
			pageSize,
			allPages,
		});
	},

	async fetchItemsSnapshot(listId, { filters = {}, page = 1, pageSize = DEFAULT_PAGE_SIZE, sort } = {}) {
		const normalizedListId = normalizeListId(listId);
		if (!normalizedListId) {
			return { items: [], pagination: {} };
		}

		const params = this.buildListQueryParams(normalizedListId, filters, {
			page,
			pageSize,
		});

		if (sort) {
			params.sort = Array.isArray(sort) ? sort.join(',') : sort;
		}

		const response = await axios.get(`${STRAPI_URL}/api/client-invoices-items`, {
			headers: this.authHeaders(),
			params,
		});

		const items = (response?.data?.data || []).map(normalizeInvoiceItem);
		const pagination = response?.data?.meta?.pagination || {};
		return {
			items,
			pagination: {
				page: pagination.page || page,
				pageSize: pagination.pageSize || pageSize,
				pageCount: pagination.pageCount || 1,
				total: pagination.total || items.length,
			},
		};
	},

		async fetchInvoiceById(documentId, { forceRefresh = false } = {}) {
			if (!documentId) return null;
			if (!forceRefresh && this.currentInvoiceCache[documentId]) {
				return this.currentInvoiceCache[documentId];
			}
			const populate = {
				client: true,
				building: true,
				client_invoices_list: true,
			};
			try {
				const response = await axios.get(`${STRAPI_URL}/api/client-invoices-items/${documentId}`, {
					headers: this.authHeaders(),
					params: { populate },
				});
				const invoice = normalizeInvoiceItem(response?.data?.data || {});
				this.currentInvoiceCache[documentId] = invoice;
				return invoice;
			} catch (error) {
				console.error('Failed to fetch invoice', error);
				throw error;
			}
		},

		async saveInvoice({ documentId, payload, refreshList = true } = {}) {
			if (!payload) return null;
			const mutablePayload = { ...payload };
			if (!documentId && !mutablePayload.invoiceNumber) {
				const documentType = normalizeDocumentType(mutablePayload.metadata || {});
				if (documentType === 'credit-note') {
					mutablePayload.invoiceNumber = await this.generateUniqueCreditNoteNumber({
						listId: mutablePayload.client_invoices_list,
						sourceInvoiceNumber: mutablePayload.metadata?.creditNote?.sourceInvoiceNumber || '',
						sourceDocumentId: mutablePayload.metadata?.creditNote?.sourceInvoiceDocumentId || '',
					});
				} else {
					mutablePayload.invoiceNumber = await this.fetchNextInvoiceNumber(mutablePayload.client_invoices_list);
				}
			}
			let savedInvoice = null;
			try {
				let response;
				let attempt = 0;
				const maxAttempts = documentId ? 1 : 4;
				while (attempt < maxAttempts) {
					try {
						const data = { data: sanitizePayloadForSave(mutablePayload) };
						if (documentId) {
							response = await axios.put(
								`${STRAPI_URL}/api/client-invoices-items/${documentId}`,
								data,
								{ headers: this.authHeaders() },
							);
						} else {
							response = await axios.post(`${STRAPI_URL}/api/client-invoices-items`, data, {
								headers: this.authHeaders(),
							});
						}
						break;
					} catch (error) {
						const shouldRetry = !documentId && isUniqueConstraintError(error) && attempt < maxAttempts - 1;
						if (!shouldRetry) throw error;
						const documentType = normalizeDocumentType(mutablePayload.metadata || {});
						if (documentType === 'credit-note') {
							mutablePayload.invoiceNumber = await this.generateUniqueCreditNoteNumber({
								listId: mutablePayload.client_invoices_list,
								sourceInvoiceNumber: mutablePayload.metadata?.creditNote?.sourceInvoiceNumber || '',
								sourceDocumentId: mutablePayload.metadata?.creditNote?.sourceInvoiceDocumentId || '',
							});
						} else {
							mutablePayload.invoiceNumber = await this.fetchNextInvoiceNumber(
								mutablePayload.client_invoices_list,
							);
						}
						attempt += 1;
					}
				}
				savedInvoice = normalizeInvoiceItem(response?.data?.data || {});
				const fullId = savedInvoice.documentId || savedInvoice.id;
				// Reload with relations so client/building remain populated in the editor
				let hydrated = savedInvoice;
				try {
					if (fullId) {
						hydrated = await this.fetchInvoiceById(fullId, { forceRefresh: true });
					}
				} catch (err) {
					console.warn('Failed to hydrate saved invoice, using lightweight response', err);
				}
				// Fallback: if relations were not returned, keep the ones we just sent
				if (!hydrated.client && payload.client) {
					hydrated.client = { id: payload.client };
				}
				if (!hydrated.building && payload.building) {
					hydrated.building = { id: payload.building };
				}
				if (!hydrated.clientSnapshot && payload.clientSnapshot) {
					hydrated.clientSnapshot = { ...payload.clientSnapshot };
				}
				if (!hydrated.invoiceContent?.dateRanges?.length && payload.invoiceContent?.dateRanges?.length) {
					hydrated.invoiceContent = {
						...(hydrated.invoiceContent || {}),
						dateRanges: payload.invoiceContent.dateRanges,
						customDates: payload.invoiceContent.customDates || [],
						summary: payload.invoiceContent.summary || hydrated.invoiceContent?.summary || {},
					};
				}
				this.currentInvoiceCache[fullId] = hydrated;
				if (hydrated?.metadata?.sourceQuotation?.documentId) {
					this.setQuotationInvoiceLinks([
						...Object.values(this.quotationInvoiceLinks || {}),
						hydrated,
					]);
					this.quotationInvoiceLinksLoaded = true;
				}
				// Refresh list cache if needed
				if (refreshList && hydrated.listId) {
					await this.fetchItemsForList(hydrated.listId);
				}
				return hydrated;
			} catch (error) {
				if (savedInvoice?.listId) {
					// Ensure loading flags are cleared if fetchItemsForList throws
					this.itemsLoadingByListId[savedInvoice.listId] = false;
				}
				console.error('Failed to save invoice', error);
				throw error;
			}
		},

	async fetchNextInvoiceNumber(listId) {
		const normalizedListId = normalizeListId(listId);
		if (!normalizedListId) return null;
		let maxNumber = null;
		let page = 1;
		let pageCount = 1;
		try {
			while (page <= pageCount && page <= MAX_NUMBER_SCAN_PAGES) {
				const response = await axios.get(`${STRAPI_URL}/api/client-invoices-items`, {
					headers: this.authHeaders(),
					params: {
						'filters[client_invoices_list][id][$eq]': normalizedListId,
						'pagination[page]': page,
						'pagination[pageSize]': 200,
						'sort[0]': 'createdAt:desc',
						'sort[1]': 'id:desc',
						fields: ['invoiceNumber'],
					},
				});
				const rows = Array.isArray(response?.data?.data) ? response.data.data : [];
				rows.forEach((entry) => {
					const attrs = entry?.attributes || entry || {};
					const orderIndex = parseDocumentOrderIndex(attrs?.invoiceNumber);
					if (orderIndex === null) return;
					maxNumber = maxNumber === null ? orderIndex : Math.max(maxNumber, orderIndex);
				});
				const pagination = response?.data?.meta?.pagination || {};
				pageCount = Number.isFinite(pagination.pageCount) && pagination.pageCount > 0
					? pagination.pageCount
					: page;
				page += 1;
			}
			return String((maxNumber ?? 0) + 1);
		} catch (error) {
			console.error('Failed to fetch next invoice number', error);
			return null;
		}
	},

	async invoiceNumberExists(invoiceNumber, { excludeDocumentId = null } = {}) {
		const normalizedInvoiceNumber = String(invoiceNumber || '').trim();
		if (!normalizedInvoiceNumber) return false;
		const response = await axios.get(`${STRAPI_URL}/api/client-invoices-items`, {
			headers: this.authHeaders(),
			params: {
				'filters[invoiceNumber][$eq]': normalizedInvoiceNumber,
				'pagination[page]': 1,
				'pagination[pageSize]': 5,
				fields: ['invoiceNumber', 'documentId'],
			},
		});
		const rows = Array.isArray(response?.data?.data) ? response.data.data : [];
		const filtered = rows.filter((entry) => {
			const attrs = entry?.attributes || entry || {};
			const docId = attrs?.documentId ?? entry?.documentId ?? null;
			if (!excludeDocumentId) return true;
			return String(docId) !== String(excludeDocumentId);
		});
		return filtered.length > 0;
	},

	async generateUniqueCreditNoteNumber({
		listId,
		sourceInvoiceNumber,
		sourceDocumentId = '',
		excludeDocumentId = null,
	} = {}) {
		const nextRaw = await this.fetchNextInvoiceNumber(listId);
		const parsedNext = parseNumericInvoiceNumber(nextRaw);
		const nextBase = parsedNext ?? 1;
		let offset = 0;
		while (offset < 500) {
			const candidate = buildCreditNoteNumber({
				nextInvoiceNumber: String(nextBase + offset),
				sourceInvoiceNumber,
				sourceDocumentId,
			});
			// eslint-disable-next-line no-await-in-loop
			const exists = await this.invoiceNumberExists(candidate, { excludeDocumentId });
			if (!exists) return candidate;
			offset += 1;
		}
		throw new Error('Unable to generate unique credit note number');
	},

	async deleteInvoice({ listId, documentId }) {
		if (!documentId) return;
		try {
			await axios.delete(`${STRAPI_URL}/api/client-invoices-items/${documentId}`, {
				headers: this.authHeaders(),
			});
			// Clear from cache
			delete this.currentInvoiceCache[documentId];
			this.setQuotationInvoiceLinks(
				Object.values(this.quotationInvoiceLinks || {}).filter(
					(invoice) => String(invoice?.documentId || invoice?.id) !== String(documentId),
				),
			);
			// Refresh list if provided
			const normalizedListId = normalizeListId(listId);
			if (normalizedListId) {
				await this.fetchItemsForList(normalizedListId, {
					filters: this.getActiveFilters(normalizedListId),
				});
			}
		} catch (error) {
			console.error('Failed to delete invoice', error);
			throw error;
		}
	},
	},
});
